import { ChartType } from '@ecosystem/newron-client-lib';
import {
  BasicTrackingFn,
  DataVizType,
  EventElementCategory,
  EventElementType,
  EventName,
  EventParams,
  Params,
  SettingsProvider,
  TrackingElementType,
} from '../types';

export const mapDataVizForChartType = (dataVizValue: string | undefined): ChartType | undefined => {
  switch (dataVizValue) {
    case DataVizType.lineChart:
      return 'Line Chart';
    case DataVizType.chart:
    case DataVizType.verticalBarChart:
    case DataVizType.horizontalBarChart:
    case DataVizType.stackedBarChart:
      return 'Bar Chart';
    case DataVizType.bubbleChart:
      return 'Bubble Chart';
    case DataVizType.table:
      return 'Table';
    default:
      return undefined;
  }
};

export const mapDataVizForElementType = (dataVizValue: string | undefined): TrackingElementType => {
  if (dataVizValue === DataVizType.table) {
    return TrackingElementType.Table;
  }
  return TrackingElementType.Chart;
};

export const addChartTypeToEventParams = (eventParams: EventParams, componentSettings: Params) => {
  if (!componentSettings?.dataViz || eventParams.chartType) {
    return eventParams;
  }
  return {
    ...eventParams,
    chartType: mapDataVizForChartType(componentSettings?.dataViz[0] as DataVizType),
  };
};

export const trackPageElementClicked = (
  trackFn: BasicTrackingFn | undefined,
  elementName: string,
  extraParams?: {
    elementType?: EventElementType;
    elementCategory?: EventElementCategory;
    [key: string]: string | string[] | undefined;
  }
): void => {
  extraParams = extraParams || {
    elementType: EventElementType.Button,
    elementCategory: EventElementCategory.CustomFilter,
  };

  const { elementType, elementCategory, ...rest } = extraParams;

  trackFn &&
    trackFn(EventName.PageElementClicked, {
      elementName,
      elementCategory: elementCategory || EventElementCategory.CustomFilter,
      elementType: elementType || EventElementType.Button,
      ...rest,
    });
};

export const getMetadataTrackingParams = (
  metadata: SettingsProvider['metadata'],
  eventParams: EventParams
): EventParams => {
  if (!metadata?.tracking) {
    return eventParams;
  }

  const { paramsMap } = metadata.tracking;

  if (!paramsMap) {
    return eventParams;
  }
  Object.keys(paramsMap).forEach(key => {
    const trackingPropertyName = paramsMap[key];
    if (!eventParams[key]) {
      return;
    }
    eventParams[trackingPropertyName] = eventParams[key];
    delete eventParams[key];
  });
  return eventParams;
};

export enum TrackGroupTypes {
  AddGroup = 'Add to group',
  RenameGroup = 'Rename Group',
  DeleteGroup = 'Delete Group',
  AddItem = 'Add more items to group',
  RemoveItem = 'Remove item from group',
}

export enum TrackTableNavTypes {
  LoadMore = 'Load more',
  Quickview = 'Quick view',
  SelectAll = 'Select All',
}

export enum TrackAnalyseTypes {
  top5 = 'Analyse Top 5',
  top10 = 'Analyse Top 10',
  selected = 'Analyse',
}

export enum TrackSortTypes {
  sort = 'Table Sort',
}

export enum TrackSelectionTypes {
  selectAll = 'Select All',
}

export type TableElementTrackTypes =
  | TrackGroupTypes
  | TrackAnalyseTypes
  | TrackSortTypes
  | TrackTableNavTypes
  | TrackSelectionTypes;

export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackGroupTypes.AddGroup | TrackGroupTypes.AddItem,
  elementType: EventElementType.Group,
  extraParams: { optionChosen: string[]; numberOfProducts: string }
): void;

export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackAnalyseTypes,
  elementType: EventElementType.Link,
  extraParams: {
    optionChosen: string[];
    numberOfProducts: string;
    selectedGroups: string[];
    numberOfGroups: string;
    includesGroups: string;
  }
): void;

export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackSortTypes.sort,
  elementType: EventElementType.Sort,
  extraParams: { optionChosen: string; column: string }
): void;

export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackGroupTypes.RenameGroup,
  elementType: EventElementType.Group
): void;
export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackGroupTypes.DeleteGroup,
  elementType: EventElementType.Group
): void;
export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackGroupTypes.RemoveItem,
  elementType: EventElementType.Group
): void;
export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackTableNavTypes.LoadMore,
  elementType: EventElementType.Link
): void;
export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackTableNavTypes.Quickview,
  elementType: EventElementType.Overlay,
  extraParams: { optionChosen: string[] }
): void;

export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TrackSelectionTypes.selectAll,
  elementType: EventElementType.CheckBox,
  extraParams: { numberOfProducts: string }
): void;

export function trackTableClicked(
  trackFn: BasicTrackingFn | undefined,
  elementName: TableElementTrackTypes,
  elementType: EventElementType,
  extraParams: EventParams = {}
): void {
  trackFn &&
    trackFn(EventName.TableClicked, {
      elementName,
      elementType,
      ...extraParams,
    });
}
